// features/auth/ForgotPassword.js - Forgot password component
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaLock } from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import LoadingOverlay from '../../components/LoadingOverlay';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { requestPasswordReset, loading, error } = useAuth();
  
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    
    try {
      await requestPasswordReset(email);
      setSuccessMessage(t('auth.passwordResetEmailSent'));
      setEmail(''); // Clear the form
    } catch (err) {
      setFormError(err.message || t('auth.passwordResetError'));
    }
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 260, 
        damping: 20 
      } 
    }
  };
  
  return (
    <div className="flex justify-center items-center min-h-[80vh] p-4">
      {loading && <LoadingOverlay />}
      
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full max-w-md"
      >
        <Card variant="glass" hover="glow" className="w-full">
          <CardHeader>
            <motion.div 
              className="w-16 h-16 rounded-2xl bg-accent bg-opacity-10 dark:bg-accent-dark dark:bg-opacity-20 flex items-center justify-center mb-4 mx-auto"
              variants={itemVariants}
            >
              <FaLock className="text-accent text-2xl" />
            </motion.div>
            <CardTitle gradient={true} className="text-center">{t('auth.forgotPasswordTitle')}</CardTitle>
            <CardDescription className="text-center">{t('auth.forgotPasswordSubtitle')}</CardDescription>
          </CardHeader>
          
          <CardContent>
            <motion.form onSubmit={handleSubmit} variants={containerVariants}>
              {formError && (
                <motion.div 
                  variants={itemVariants}
                  className="bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded relative mb-4"
                >
                  {formError}
                </motion.div>
              )}
              
              {successMessage && (
                <motion.div 
                  variants={itemVariants}
                  className="bg-green-100 dark:bg-green-900/30 border border-green-400 text-green-700 dark:text-green-300 px-4 py-3 rounded relative mb-4"
                >
                  {successMessage}
                </motion.div>
              )}
              
              <motion.div className="mb-6" variants={itemVariants}>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder={t('auth.emailPlaceholder')}
                  required
                />
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Button
                  type="submit"
                  variant="accent"
                  className="w-full"
                  animation="shimmer"
                  disabled={loading}
                >
                  {loading ? t('common.loading') : t('auth.sendResetLink')}
                </Button>
              </motion.div>
            </motion.form>
          </CardContent>
          
          <CardFooter className="flex justify-center">
            <motion.p variants={itemVariants} className="text-sm text-center">
              <Link to="/login" className="text-accent hover:underline">
                {t('auth.backToLogin')}
              </Link>
            </motion.p>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
